import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Table, TableCell, TableHead, TableRow } from '@mui/material'
import classNames from 'classnames'
import * as R from 'ramda'
import { Defaults, PermissionArea } from '@pbt/pbt-ui-components'

import NoRecords from '~/components/dashboard/landing/widgets/NoRecords'
import { getPlaceHolders } from '~/components/dashboard/landing/widgets/TableWidget'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import {
  fetchClientConversationsList,
  fetchMoreItemsForClientConversationsList,
  setConversationsListFilters,
} from '~/store/actions/conversations'
import { getCRUDByArea } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getConversationListFetching,
  getConversationsError,
  getConversationsListFilters,
  getConversationsListObjects,
  getConversationsTotalCount,
} from '~/store/reducers/conversations'
import useDialog from '~/utils/useDialog'

import { communicationsSnapshotColumns } from './communicationsSnapshotColumns'
import ConversationErrorAlert from './ConversationErrorAlert'
import ConversationRow from './ConversationRow'
import RowSkeleton from './RowSkeleton'
import SnapshotHeader from './SnapshotHeader'

import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './CommunicationSnapshot.module.scss'

const CommunicationsSnapshot = ({
  clientId,
  patientId,
  isExpanded,
  onExpand,
  rowsCount,
}: {
  clientId: string
  isExpanded?: boolean
  onExpand: () => void
  patientId?: string
  rowsCount: number
}) => {
  const { t } = useTranslation(['Common', 'Clients'])
  const dispatch = useDispatch()
  const isSuppressAllSystemMessagesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SUPPRESS_ALL_SYSTEM_MESSAGES),
  )
  const conversationError = useSelector(getConversationsError)
  const conversations = useSelector(getConversationsListObjects)
  const conversationsTotalCount = useSelector(getConversationsTotalCount)
  const filters = useSelector(getConversationsListFilters)
  const isLoadingConversations = useSelector(getConversationListFetching)
  const { create: communicationCreatePermissions } = useSelector(
    getCRUDByArea(PermissionArea.COMMUNICATION),
  )
  const [openNewConversationDialog] = useDialog(
    DialogNames.NEW_CONVERSATION_WITH_STEPS,
  )
  const placeholderRows = getPlaceHolders(
    rowsCount || Defaults.CONVERSATION_MESSAGES_BATCH_LOAD_COUNT,
  )

  const handleNewConversationClick = () => {
    openNewConversationDialog({ clientId, patientId })
  }

  const fetchConversations = () => {
    dispatch(fetchClientConversationsList(clientId, 0, rowsCount, false))
  }

  const fetchMoreItems = (startIndex: number, endIndex: number) => {
    dispatch(
      fetchMoreItemsForClientConversationsList(clientId, startIndex, endIndex),
    )
  }

  useEffect(() => {
    const excludeSystemMessagesFilter = filters?.excludeSystemMessages ?? {
      value: isSuppressAllSystemMessagesEnabled,
    }

    dispatch(
      setConversationsListFilters({
        ...filters,
        category: { value: null },
        excludeSystemMessages: excludeSystemMessagesFilter,
        patientIds: { value: patientId ? [patientId] : null },
      }),
    )
  }, [patientId])

  useEffect(() => {
    fetchConversations()
  }, [clientId, filters])

  const showNoItems =
    !conversationError &&
    !isLoadingConversations &&
    R.isEmpty(conversations) &&
    conversationsTotalCount === 0
  const showTable = !conversationError && !showNoItems
  const totalCount =
    isLoadingConversations && !isExpanded ? 0 : conversationsTotalCount
  const displayCount = conversations.length

  return (
    <Grid
      className={classNames(styles.snapshot, {
        [styles.expandedTableView]: Boolean(isExpanded),
      })}
    >
      <SnapshotHeader
        isExpanded={isExpanded}
        name={t('Common:COMMUNICATIONS')}
        showNoItems={showNoItems}
        onExpand={onExpand}
      />
      {conversationError && (
        <ConversationErrorAlert onClick={fetchConversations} />
      )}
      {showNoItems && (
        <NoRecords
          action={t('Clients:SNAPSHOTS_AND_RECORDS.ADD_CONVERSATION')}
          text={t('Clients:SNAPSHOTS_AND_RECORDS.NO_COMMUNICATIONS')}
          onClick={
            communicationCreatePermissions
              ? handleNewConversationClick
              : undefined
          }
        />
      )}
      {showTable && (
        <Table className={styles.table} size="small">
          <caption>
            {t('Clients:COMMUNICATION_WIDGET.THREAD_COUNTER', {
              displayCount,
              totalCount,
            })}
          </caption>
          <TableHead className={styles.tableHeader}>
            <TableRow>
              {communicationsSnapshotColumns.map(({ label, id }) => (
                <TableCell className={styles[id]} key={id}>
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <InfiniteScroll
            element="tbody"
            hasMore={isExpanded && displayCount < conversationsTotalCount}
            initialLoad={false}
            loadMore={() =>
              fetchMoreItems(
                displayCount,
                displayCount + Defaults.CONVERSATION_MESSAGES_BATCH_LOAD_COUNT,
              )
            }
            loader={
              <RowSkeleton
                columns={communicationsSnapshotColumns}
                rows={placeholderRows}
              />
            }
          >
            {isLoadingConversations && !isExpanded ? (
              <RowSkeleton
                columns={communicationsSnapshotColumns}
                rows={placeholderRows}
              />
            ) : (
              conversations.map((item) => (
                <ConversationRow conversation={item} key={item.id} />
              ))
            )}
          </InfiniteScroll>
        </Table>
      )}
    </Grid>
  )
}

export default CommunicationsSnapshot
